<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <oxd-table-filter :filter-title="$t('expensa.menu_dashboard')">
    <oxd-form @submit-valid="load">
      <oxd-form-row>
        <oxd-grid :cols="4" class="orangehrm-full-width-grid">
          <oxd-grid-item>
            <date-input
              v-model="filters.fromDate"
              :label="$t('general.from_date')"
              :rules="rules.fromDate"
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <date-input
              v-model="filters.toDate"
              :label="$t('general.to_date')"
              :rules="rules.toDate"
            />
          </oxd-grid-item>
          <oxd-grid-item v-if="showEmployeeFilter">
            <employee-autocomplete
              v-model="filters.employee"
              :rules="rules.employee"
              :params="{includeEmployees: 'currentAndPast'}"
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <oxd-input-field
              v-model="filters.category"
              type="select"
              :label="$t('expensa.col_category')"
              :options="categoryOptions"
            />
          </oxd-grid-item>
        </oxd-grid>
      </oxd-form-row>
      <oxd-divider />
      <oxd-form-actions>
        <oxd-button
          display-type="ghost"
          :label="$t('general.reset')"
          @click="onClickReset"
        />
        <oxd-button
          class="orangehrm-left-space"
          display-type="secondary"
          :label="$t('general.search')"
          type="submit"
        />
      </oxd-form-actions>
    </oxd-form>
  </oxd-table-filter>
  <br />
  <div class="orangehrm-paper-container">
    <oxd-text v-if="loading" tag="p" class="orangehrm-container expensa-note">
      {{ $t('expensa.loading') }}
    </oxd-text>
    <oxd-text
      v-else-if="loadError"
      tag="p"
      class="orangehrm-container expensa-note"
    >
      {{ loadError }}
    </oxd-text>
    <div v-else-if="summary" class="orangehrm-container">
      <div class="expensa-kpis">
        <div v-for="k in kpiCards" :key="k.key" class="expensa-kpi">
          <oxd-text tag="p" class="expensa-kpi-label">{{ k.label }}</oxd-text>
          <oxd-text tag="p" class="expensa-kpi-value">{{ k.value }}</oxd-text>
        </div>
      </div>
      <div class="expensa-sections">
        <section v-for="s in sections" :key="s.key" class="expensa-section">
          <oxd-text tag="h6" class="expensa-section-title">
            {{ s.title }}
          </oxd-text>
          <bar-list
            :items="s.items"
            :unit="s.unit"
            :empty-text="$t('expensa.dash_no_data')"
          />
        </section>
        <section class="expensa-section">
          <oxd-text tag="h6" class="expensa-section-title">
            {{ $t('expensa.dash_violations') }}
          </oxd-text>
          <ul class="expensa-plain-list">
            <li v-if="summary.violations.length === 0">
              {{ $t('expensa.dash_no_data') }}
            </li>
            <li v-for="v in summary.violations" :key="v.code">
              <code>{{ v.code }}</code> · {{ v.count }}
              {{ $t('expensa.dash_unit_expenses') }}
            </li>
          </ul>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import {ref} from 'vue';
import EmployeeAutocomplete from '@/core/components/inputs/EmployeeAutocomplete.vue';
import useDateFormat from '@/core/util/composable/useDateFormat';
import useEmployeeNameTranslate from '@/core/util/composable/useEmployeeNameTranslate';
import {
  endDateShouldBeAfterStartDate,
  required,
  validDateFormat,
  validSelection,
} from '@/core/util/validation/rules';
import BarList from '@/orangehrmExpensaPlugin/components/BarList.vue';
import {expensaApi, errorMessage} from '../util/api';
import {defaultPeriod, monthLabel} from '../util/dashboard';
import {eur} from '../util/format';
import {kindKey, reportStatusKey} from '../util/labels';

const initialFilters = () => ({
  ...defaultPeriod(),
  employee: null,
  category: null,
});

export default {
  name: 'ExpensaDashboard',
  components: {
    'bar-list': BarList,
    'employee-autocomplete': EmployeeAutocomplete,
  },
  setup() {
    const {userDateFormat} = useDateFormat();
    const {$tEmpName} = useEmployeeNameTranslate();
    const filters = ref(initialFilters());
    return {userDateFormat, $tEmpName, filters};
  },
  data() {
    return {
      me: null,
      categories: [],
      summary: null,
      loading: false,
      loadError: null,
      requestSeq: 0,
    };
  },
  computed: {
    rules() {
      return {
        employee: [validSelection],
        fromDate: [required, validDateFormat(this.userDateFormat)],
        toDate: [
          required,
          validDateFormat(this.userDateFormat),
          endDateShouldBeAfterStartDate(
            () => this.filters.fromDate,
            this.$t('general.to_date_should_be_after_from_date'),
            {allowSameDate: true},
          ),
        ],
      };
    },
    showEmployeeFilter() {
      return !!this.me && (this.me.isFinance || this.me.isSupervisor);
    },
    categoryOptions() {
      return this.categories.map((c) => ({id: c.id, label: c.name}));
    },
    kpiCards() {
      const k = this.summary.kpis;
      const card = (key, label, value) => ({key, label, value});
      return [
        card('count', this.$t('expensa.dash_kpi_count'), String(k.count)),
        card('total', this.$t('expensa.dash_kpi_total'), eur(k.totalEur)),
        card('average', this.$t('expensa.dash_kpi_average'), eur(k.averageEur)),
        card(
          'reimbursable',
          this.$t('expensa.dash_kpi_reimbursable'),
          eur(k.reimbursableEur),
        ),
        card('vat', this.$t('expensa.dash_kpi_vat'), eur(k.vatDeductibleEur)),
        card(
          'violations',
          this.$t('expensa.dash_kpi_violations'),
          String(k.withViolations),
        ),
        card(
          'review',
          this.$t('expensa.dash_kpi_review'),
          String(k.needsReview),
        ),
        card(
          'unreported',
          this.$t('expensa.dash_kpi_unreported'),
          String(k.unreported),
        ),
        card(
          'toApprove',
          this.$t('expensa.dash_kpi_to_approve'),
          String(k.reportsToApprove),
        ),
        card(
          'toReimburse',
          this.$t('expensa.dash_kpi_to_reimburse'),
          eur(k.toReimburseEur),
        ),
      ];
    },
    sections() {
      const s = this.summary;
      const unit = this.$t('expensa.dash_unit_expenses');
      const item = (key, label, row) => ({
        key,
        label,
        amount: row.totalEur,
        count: row.count,
      });
      return [
        {
          key: 'month',
          title: this.$t('expensa.dash_by_month'),
          unit,
          items: s.byMonth.map((r) => item(r.month, monthLabel(r.month), r)),
        },
        {
          key: 'category',
          title: this.$t('expensa.dash_by_category'),
          unit,
          items: s.byCategory.map((r) => item(r.code, r.name, r)),
        },
        {
          key: 'employee',
          title: this.$t('expensa.dash_by_employee'),
          unit,
          items: s.byEmployee.map((r) =>
            item(String(r.empNumber), this.$tEmpName(r), r),
          ),
        },
        {
          key: 'kind',
          title: this.$t('expensa.dash_by_kind'),
          unit,
          items: s.byKind.map((r) => item(r.kind, this.$t(kindKey(r.kind)), r)),
        },
        {
          key: 'merchant',
          title: this.$t('expensa.dash_top_merchants'),
          unit,
          items: s.topMerchants.map((r) => item(r.merchant, r.merchant, r)),
        },
        {
          key: 'status',
          title: this.$t('expensa.dash_reports_by_status'),
          unit: this.$t('expensa.dash_unit_reports'),
          items: s.reportsByStatus.map((r) =>
            item(r.status, this.$t(reportStatusKey(r.status, false)), r),
          ),
        },
      ];
    },
  },
  beforeMount() {
    expensaApi('/me')
      .getAll()
      .then(({data}) => {
        this.me = data.data;
      })
      .catch(() => {
        // senza `me` il filtro dipendente resta nascosto: la dashboard funziona comunque
      });
    expensaApi('/categories')
      .getAll({includeInactive: true})
      .then(({data}) => {
        this.categories = data.data;
      })
      .catch(() => {
        // senza categorie il filtro resta vuoto
      });
    this.load();
  },
  methods: {
    async load() {
      const seq = ++this.requestSeq;
      this.loading = true;
      this.loadError = null;
      const params = {
        fromDate: this.filters.fromDate,
        toDate: this.filters.toDate,
      };
      if (this.filters.employee) params.empNumber = this.filters.employee.id;
      if (this.filters.category) params.categoryId = this.filters.category.id;
      try {
        const {data} = await expensaApi('/analytics').getAll(params);
        if (seq !== this.requestSeq) return;
        this.summary = data.data;
      } catch (e) {
        if (seq !== this.requestSeq) return;
        this.summary = null;
        // anche il periodo oltre un anno torna come testo d'errore del server
        this.loadError = errorMessage(e);
      } finally {
        if (seq === this.requestSeq) this.loading = false;
      }
    },
    onClickReset() {
      this.filters = initialFilters();
      this.load();
    },
  },
};
</script>

<style scoped lang="scss">
.expensa-note {
  padding: 1.2rem;
}
.expensa-kpis {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(10rem, 1fr));
  gap: 0.75rem;
  margin-bottom: 1.5rem;
}
.expensa-kpi {
  border: 1px solid #e8eaef;
  border-radius: 0.5rem;
  padding: 0.75rem;
}
.expensa-kpi-label {
  font-size: 0.75rem;
  opacity: 0.75;
}
.expensa-kpi-value {
  font-size: 1.25rem;
  font-weight: 700;
}
.expensa-sections {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(20rem, 1fr));
  gap: 1.5rem;
}
.expensa-section-title {
  font-weight: 700;
  margin-bottom: 0.75rem;
}
.expensa-plain-list {
  list-style: none;
  margin: 0;
  padding: 0;
  font-size: 0.8125rem;
  li {
    margin-bottom: 0.4rem;
  }
}
</style>
